import React, { useRef } from 'react';
import { Pie } from 'react-chartjs-2';
import { LoadingSpinner, ErrorMessage, EmptyState, ChartControls } from '../ui';
import { useCustomerLoyaltyDistribution, SectionProps } from '../../hooks/useCustomerAnalyticsSections';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor, mergeChartOptions } from '../../utils/themeHelpers';
import { formatNumber } from '../../utils/formatters';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface NoOrdersLastTwoMonthsProps extends SectionProps {
  branchId: number;
}

const NoOrdersLastTwoMonths: React.FC<NoOrdersLastTwoMonthsProps> = ({
  branchId,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: loyaltyData, isLoading, isError, error, refetch } = useCustomerLoyaltyDistribution(branchId);

  // Component metadata
  const componentId = 'no-orders-last-two-months';
  const componentTitle = 'عدم سفارش در دو ماه گذشته';

  // Handle error callback
  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
                    <div className="flex flex-col items-center justify-center h-[350px]">
                      <LoadingSpinner
                        variant="minimal"
                        size="lg"
                        message="در حال بارگذاری…"
                      />
                    </div>
                  </div>
    );
  }

  if (isError && error) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage 
          message="خطا در دریافت اطلاعات"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (!loyaltyData?.noOrdersLastTwoMonths) {
    return (
      <div className="card-base card-padding">
        <EmptyState />
      </div>
    );
  }

  const pieData = {
    labels: ['یک سفارش', 'دو یا سه سفارش', 'بیش از چهار سفارش'],
    datasets: [
      {
        data: [
          loyaltyData.noOrdersLastTwoMonths.oneOrder,
          loyaltyData.noOrdersLastTwoMonths.twoThreeOrders,
          loyaltyData.noOrdersLastTwoMonths.fourPlusOrders
        ],
        backgroundColor: [
          getChartBackgroundColor(0, currentTheme, 0.8),
          getChartBackgroundColor(1, currentTheme, 0.8),
          getChartBackgroundColor(2, currentTheme, 0.8)
        ],
        borderColor: [
          getChartColors(currentTheme)[0],
          getChartColors(currentTheme)[1],
          getChartColors(currentTheme)[2]
        ],
        borderWidth: 2
      }
    ]
  };

  const pieOptions = mergeChartOptions(getChartJsOptions(currentTheme), {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        display: false
      },
      y: {
        display: false
      }
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        align: 'start' as const
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            const separator = '\u00A0:\u00A0';
            const formattedValue = `${formatNumber(value)} (${percentage}%)`;
            return `${label}${separator}${formattedValue}`;
          }
        }
      }
    }
  });

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <div className="mb-6">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="chart"
          dashboardSource="customerAnalytics"
          onInfo={() => openGuide(CHART_GUIDES.noOrdersLastTwoMonths)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'customerAnalytics',
            componentType: 'chart',
            description: 'تحلیل توزیع مشتریان بدون سفارش در دو ماه گذشته',
            props: { branchId, compact },
            gridLayout: {
              containerClass: 'col-span-1'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
        />
      </div>
      <h3 className="title-card">
        عدم سفارش در دو ماه گذشته
      </h3>
      <div style={{ height: 350, width: '100%' }}>
        <Pie data={pieData} options={pieOptions} />
      </div>
    </div>
  );
};

export default NoOrdersLastTwoMonths;